import { useRef, useState, useEffect } from 'react';
import { hapticError, hapticLight, hapticMedium } from '@client/lib/haptics';
import { cn } from '@client/lib/utils';
import { X, Check } from 'lucide-react';

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface CameraProps {
  onCapture: (blob: Blob, onProgress: (progress: UploadProgress) => void) => Promise<void>;
  onCancel: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Camera({ onCapture, onCancel }: CameraProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      onCancel();
      return;
    }

    // Create preview
    const url = URL.createObjectURL(file);
    setPreview(url);
    setCapturedBlob(file);
  };

  const confirmCapture = async () => {
    if (!capturedBlob) return;

    hapticMedium();
    setUploading(true);
    setUploadProgress({ loaded: 0, total: capturedBlob.size, percentage: 0 });

    try {
      await onCapture(capturedBlob, (progress) => {
        setUploadProgress(progress);
      });
    } catch (error) {
      hapticError();
      console.error('Error uploading:', error);
      alert('Upload fehlgeschlagen. Bitte versuche es nochmal.');
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const retake = () => {
    hapticLight();
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setCapturedBlob(null);
    setUploadProgress(null);
    // Trigger file input again
    fileInputRef.current?.click();
  };

  // If no preview yet, show the file input trigger
  if (!preview) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />
        {/* Auto-trigger on mount */}
        <TriggerCapture inputRef={fileInputRef} onCancel={onCancel} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <div className="relative h-full">
        {/* Preview */}
        <img
          src={preview}
          alt="Captured"
          className="w-full h-full object-contain"
        />

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 pt-safe px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => {
              hapticLight();
              onCancel();
            }}
            disabled={uploading}
            className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Photo size indicator */}
          {capturedBlob && !uploading && (
            <div className="bg-black/50 px-3 py-1.5 rounded-full">
              <span className="text-white text-[13px]">
                {formatBytes(capturedBlob.size)}
              </span>
            </div>
          )}
        </div>

        {/* Upload Progress Overlay */}
        {uploading && uploadProgress && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
            <div className="w-64 space-y-4">
              <div className="text-center">
                <p className="text-white text-[17px] font-semibold mb-1">Wird hochgeladen...</p>
                <p className="text-white/70 text-[15px]">
                  {formatBytes(uploadProgress.loaded)} / {formatBytes(uploadProgress.total)}
                </p>
              </div>
              
              {/* Progress bar */}
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-150 ease-out"
                  style={{ width: `${uploadProgress.percentage}%` }}
                />
              </div>
              
              <p className="text-center text-white text-[22px] font-bold">
                {uploadProgress.percentage}%
              </p>
            </div>
          </div>
        )}

        {/* Bottom controls */}
        <div className="absolute bottom-0 left-0 right-0 pb-safe px-4 py-8">
          <div className="flex justify-center gap-8">
            <button
              onClick={retake}
              disabled={uploading}
              className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center disabled:opacity-50"
            >
              <X className="w-8 h-8 text-white" />
            </button>
            <button
              onClick={confirmCapture}
              disabled={uploading}
              className={cn(
                'w-20 h-20 rounded-full bg-primary flex items-center justify-center',
                uploading && 'opacity-50'
              )}
            >
              <Check className="w-10 h-10 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component to trigger file input on mount
function TriggerCapture({ 
  inputRef, 
  onCancel 
}: { 
  inputRef: React.RefObject<HTMLInputElement | null>;
  onCancel: () => void;
}) {
  const triggered = useRef(false);
  const hasFile = useRef(false);

  useEffect(() => {
    // Track if file was selected
    const input = inputRef.current;
    if (!input) return;

    const handleChange = () => {
      hasFile.current = (input.files?.length ?? 0) > 0;
    };
    
    input.addEventListener('change', handleChange);
    return () => input.removeEventListener('change', handleChange);
  }, [inputRef]);

  useEffect(() => {
    // Trigger file input on mount
    if (!triggered.current) {
      triggered.current = true;
      setTimeout(() => {
        inputRef.current?.click();
      }, 100);
    }

    // Detect when user returns to page without selecting a file
    // This works on iOS when the camera/photo picker is dismissed
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to allow file input change event to fire first
        setTimeout(() => {
          if (!hasFile.current && !inputRef.current?.files?.length) {
            onCancel();
          }
        }, 300);
      }
    };

    // Also handle focus for desktop browsers
    const handleWindowFocus = () => {
      setTimeout(() => {
        if (!hasFile.current && !inputRef.current?.files?.length) {
          onCancel();
        }
      }, 300);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [inputRef, onCancel]);

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
      <p className="text-white/70 mt-4 text-[15px]">Kamera wird geöffnet...</p>
    </div>
  );
}
